import type { ComponentType } from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { DataResponse } from "../hooks/useGetGeneralData";
import AirRoundedIcon from "@mui/icons-material/AirRounded";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import OpacityRoundedIcon from "@mui/icons-material/OpacityRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ThermostatRoundedIcon from "@mui/icons-material/ThermostatRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";

export interface ParameterDefinition {
  keys: readonly (keyof DataResponse)[];
  label: string;
  unit?: string;
  description?: string;
  source?: string;
  icon: ComponentType<SvgIconProps>;
  yAxisRange?: { min: number; max: number };
}

/**
 * Catálogo visible de parámetros. Las claves son los contratos actuales del
 * backend; los aliases permiten presentar una misma magnitud sin duplicarla.
 */
export const PARAMETER_CATALOG: readonly ParameterDefinition[] = [
  {
    keys: ["temperatura", "temperatura_agua"],
    label: "Temperatura del agua",
    unit: "°C",
    description: "Temperatura del agua registrada por la fuente de datos.",
    icon: ThermostatRoundedIcon,
    yAxisRange: { min: 5, max: 30 },
  },
  {
    keys: ["temperatura_nasa"],
    label: "Temperatura del agua (NASA)",
    unit: "°C",
    description: "Estimación de temperatura del agua provista por NASA.",
    icon: ThermostatRoundedIcon,
    yAxisRange: { min: 5, max: 30 },
  },
  {
    keys: ["salinidad"],
    label: "Salinidad",
    unit: "PSU",
    description: "Cantidad relativa de sales disueltas, expresada en PSU.",
    icon: WaterDropRoundedIcon,
    yAxisRange: { min: 0, max: 34 },
  },
  {
    keys: ["coliformes_termotolerantes"],
    label: "Coliformes termotolerantes",
    unit: "NMP/100 ml",
    description: "Recuento informado por la fuente. No constituye por sí solo un dictamen sanitario.",
    icon: ScienceRoundedIcon,
  },
  {
    keys: ["enterococos_ecocos"],
    label: "Enterococos (muestra)",
    unit: "NMP/100 ml",
    description: "Recuento de enterococos correspondiente a una muestra puntual, según la fuente.",
    icon: ScienceRoundedIcon,
  },
  {
    keys: ["enterococos_mg5"],
    label: "Enterococos (media de 5)",
    unit: "NMP/100 ml",
    description: "Valor de enterococos calculado sobre una ventana de cinco muestras, según la fuente.",
    icon: ScienceRoundedIcon,
  },
  {
    keys: ["turbidez"],
    label: "Turbidez",
    unit: "NTU",
    description: "Medida de la dispersión de la luz causada por partículas en el agua, según la unidad reportada.",
    icon: OpacityRoundedIcon,
    yAxisRange: { min: 0, max: 150 },
  },
  {
    keys: ["oxigeno_disuelto"],
    label: "Oxígeno disuelto",
    unit: "mg/L",
    description: "Concentración de oxígeno disuelto en el agua, expresada en miligramos por litro.",
    icon: BubbleChartRoundedIcon,
  },
  {
    keys: ["temperatura_ambiente"],
    label: "Temperatura ambiente",
    unit: "°C",
    description: "Temperatura del aire o del ambiente informada por la fuente.",
    icon: ThermostatRoundedIcon,
    yAxisRange: { min: 5, max: 30 },
  },
  {
    keys: ["presion_atmosferica"],
    label: "Presión atmosférica",
    unit: "hPa",
    description: "Presión atmosférica informada por la fuente, expresada en hectopascales.",
    icon: SpeedRoundedIcon,
  },
  {
    keys: ["velocidad_viento"],
    label: "Velocidad de viento",
    unit: "m/s",
    description: "Velocidad del viento informada por la fuente, expresada en metros por segundo.",
    icon: AirRoundedIcon,
  },
  {
    keys: ["ola_altura_significativa"],
    label: "Altura significativa de ola",
    unit: "m",
    description: "Altura significativa de ola informada por la fuente.",
    icon: WavesRoundedIcon,
  },
  {
    keys: ["ola_altura_maxima"],
    label: "Altura máxima de ola",
    unit: "m",
    description: "Altura máxima de ola informada por la fuente.",
    icon: WavesRoundedIcon,
  },
  {
    keys: ["ola_periodo_pico"],
    label: "Período pico de ola",
    unit: "s",
    description: "Período pico de ola informado por la fuente, expresado en segundos.",
    icon: WavesRoundedIcon,
  },
  {
    keys: ["direccion_viento"],
    label: "Dirección del viento",
    unit: "°",
    description: "Dirección del viento informada por la fuente, expresada en grados.",
    icon: ExploreRoundedIcon,
  },
  {
    keys: ["velocidad_corriente"],
    label: "Velocidad de corriente",
    unit: "m/s",
    description: "Velocidad de la corriente informada por la fuente, expresada en metros por segundo.",
    icon: SpeedRoundedIcon,
  },
  {
    keys: ["direccion_corriente"],
    label: "Dirección de corriente",
    unit: "°",
    description: "Dirección de la corriente informada por la fuente, expresada en grados.",
    icon: ExploreRoundedIcon,
  },
  {
    keys: ["ola_direccion_media"],
    label: "Dirección media de ola",
    unit: "°",
    description: "Dirección media de las olas informada por la fuente, expresada en grados.",
    icon: ExploreRoundedIcon,
  },
  {
    keys: ["nivel_mar"],
    label: "Nivel del mar",
    unit: "m",
    description: "Nivel del mar reportado por la fuente. La referencia vertical debe consultarse en sus metadatos.",
    icon: WavesRoundedIcon,
  },
  {
    keys: ["clorofila"],
    label: "Clorofila",
    unit: "µg/L",
    description: "Concentración de clorofila informada por la fuente, expresada en microgramos por litro.",
    icon: BubbleChartRoundedIcon,
    yAxisRange: { min: 0, max: 40 },
  },
  {
    keys: ["ph"],
    label: "pH",
    unit: "pH",
    description: "Medida de acidez o alcalinidad informada por la fuente.",
    icon: ScienceRoundedIcon,
    yAxisRange: { min: 6, max: 9 },
  },
];

export const getParameterDefinition = (key?: string): ParameterDefinition | undefined => {
  if (!key) return undefined;
  return PARAMETER_CATALOG.find((parameter) => parameter.keys.some((candidate) => String(candidate) === key));
};

export const getParameterLabel = (key?: string): string | undefined => getParameterDefinition(key)?.label;

export const getParameterUnit = (key?: string): string | undefined => getParameterDefinition(key)?.unit;
